/**
 * LinkGraphPage - メモ・本・カードのつながりの全体図
 *
 * - 全体: すべての本・カード・メモと、リンク・引用の線
 * - 本どうし: 同じカード・メモとつながっている本どうしを結んだ図と、その組の一覧
 */

import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { getLinkGraph, type LinkGraph } from '@/api/services/links'
import { Header } from '@/components/shared/header'
import { Spinner } from '@/components/ui/spinner'
import { useGoBack } from '@/hooks/useGoBack'
import { bookPairGraph, bookPairs, GRAPH_NODE_COLORS, graphNodePath } from '@/lib/link-graph'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'

const LinkGraphCanvas = lazy(() => import('@/components/memo/link-graph-canvas'))

type View = 'all' | 'books'

const VIEWS: { value: View; label: string }[] = [
  { value: 'all', label: '全体' },
  { value: 'books', label: '本どうし' },
]

const LEGEND = [
  { type: 'book', label: '本' },
  { type: 'card', label: 'カード' },
  { type: 'memo', label: 'メモ' },
  { type: 'todo', label: 'Todo' },
] as const

export const LinkGraphPage = () => {
  const navigate = useNavigate()
  const goBack = useGoBack(PATHS.MEMO.LIST)
  const [view, setView] = useState<View>('all')
  const [graph, setGraph] = useState<LinkGraph | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    getLinkGraph()
      .then(setGraph)
      .catch(() => setFailed(true))
  }, [])

  const pairs = useMemo(() => (graph ? bookPairs(graph) : []), [graph])
  const shown = useMemo(() => {
    if (!graph) return null
    return view === 'all' ? graph : bookPairGraph(graph, pairs)
  }, [graph, view, pairs])

  // 一覧の下に置くので、図は画面の高さの 6 割にとどめる
  const height = Math.round(window.innerHeight * 0.6)

  return (
    <div className="flex h-dvh flex-col">
      <Header title="つながり" onClose={goBack} />
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div className="flex gap-1 text-xs">
            {VIEWS.map((v) => (
              <button
                key={v.value}
                type="button"
                aria-pressed={view === v.value}
                onClick={() => setView(v.value)}
                className={cn(
                  'rounded-full border px-3 py-1',
                  view === v.value ? 'border-primary bg-primary text-primary-foreground' : 'text-muted-foreground',
                )}
              >
                {v.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
            {LEGEND.filter((l) => view === 'all' || l.type === 'book').map((l) => (
              <span key={l.type} className="flex items-center gap-1">
                <span className="size-2.5 rounded-full" style={{ backgroundColor: GRAPH_NODE_COLORS.get(l.type) }} />
                {l.label}
              </span>
            ))}
            {view === 'all' && (
              <span className="flex items-center gap-1">
                <span className="h-0.5 w-4 bg-amber-500/60" />
                ハイライトの引用
              </span>
            )}
          </div>
        </div>

        {failed ? (
          <p className="py-10 text-center text-sm text-muted-foreground">読み込めませんでした</p>
        ) : !shown ? (
          <div className="flex justify-center py-10">
            <Spinner size={24} />
          </div>
        ) : shown.nodes.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            {view === 'all'
              ? 'まだつながりがありません。メモで「[[」を使ってリンクするか、ハイライトを引用してください'
              : '同じカードやメモとつながっている本の組はまだありません'}
          </p>
        ) : (
          <Suspense fallback={<div style={{ height }} />}>
            <LinkGraphCanvas
              key={view}
              graph={shown}
              height={height}
              onNodeClick={(node) => {
                const path = graphNodePath(node)
                if (path) navigate(path)
              }}
            />
          </Suspense>
        )}

        {view === 'books' && pairs.length > 0 && (
          <ul className="mt-4 space-y-2">
            {pairs.map((pair) => (
              <li key={`${pair.source.id}|${pair.target.id}`} className="rounded-md border px-3 py-2 text-sm">
                <p className="font-medium">
                  {pair.source.title} <span className="text-muted-foreground">×</span> {pair.target.title}
                </p>
                {pair.via.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">共通: {pair.via.map((n) => n.title).join('、')}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
